import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useParams } from 'react-router-dom'
import {
	getEmployeeWorkItems,
	getEmployeeWorkItemChildren,
	getDirectReportWorkItemChildren,
	getDirectReportSearchMatches,
	assignWorkItems as assignWork,
	unassignWorkItem as unassignWork,
} from '../../services/workItemService'
import { Employee } from '../../types/Employee/Employee'
import { WorkItem } from '../../types/Work/WorkItem'
import { WorkItemStatus } from '../../types/Work/WorkItemStatus'
import { usePermissionContext } from '../../security/PermissionContext'
import { useTokenClaims } from '../../security/useTokenClaims'
import { LazyWorkItemTree, useLazyWorkItemTree } from '../core/lazyTree/useLazyWorkItemTree'
import { buildLoadedDescendantMap } from './visibleAssignRows'
interface AssignWorkContextType {
	directReports: Employee[] | undefined
	workItems: WorkItem[] | undefined
	workItemTree: LazyWorkItemTree
	selectedWorkerTree: LazyWorkItemTree
	selectedWorkerSearchHighlightIds: ReadonlySet<number>
	unassignWorkItem: (workItem: WorkItem, fromEmployeeId: number) => Promise<boolean>
	assignWorkItems: (
		workItemId: number,
		toEmployeeId: number,
		fromEmployeeId: number,
		assignAction: boolean,
	) => Promise<boolean>
	loadData: (unassignPage?: boolean) => Promise<void>
	loadingData: boolean
	setSelectedWorker: React.Dispatch<React.SetStateAction<Employee | undefined>>
	selectedWorker: Employee | undefined
	setSearchText: React.Dispatch<React.SetStateAction<string>>
	setPage: React.Dispatch<React.SetStateAction<number | undefined>>
	wiCount: number
	page: number | undefined
	setLastId: React.Dispatch<React.SetStateAction<number | undefined>>
	searchText: string
	currentEmployee: Employee | undefined
	wiSelectMode: 'unassign' | 'assign' | null
	setWiSelectMode: React.Dispatch<React.SetStateAction<'unassign' | 'assign' | null>>
	selectedWorkItems: number[]
	addSelectedWorkItem: (workItemId: number) => void
	removeSelectedWorkItem: (workItemId: number) => void
	setSelectedWorkItems: React.Dispatch<React.SetStateAction<number[]>>
	adjacentSelectedIds: number[]
	statusFilter: WorkItemStatus
	setStatusFilter: React.Dispatch<React.SetStateAction<WorkItemStatus>>
}

const AssignWorkContext = createContext<AssignWorkContextType | null>(null)

const useAssignWorkContext = (): AssignWorkContextType => {
	const assignWorkContextState = useContext(AssignWorkContext)
	if (assignWorkContextState === null) {
		throw new Error(
			'AssignWorkContext not found. Try wrapping a parent component with <AssignWorkContext.Provider>.',
		)
	}
	return assignWorkContextState
}

type AssignWorkProviderProps = {
	children?: React.ReactNode
}

const AssignWorkProvider = ({ children }: AssignWorkProviderProps) => {
	const params = useParams()
	// Prefer the URL over React lastId — params.id updates on the same render as navigation;
	// lastId only updates in a WorkItemsPage effect and lagged one cycle (stale currentEmployee fetch).
	const pageWorkerId = params.id ? parseInt(params.id, 10) : undefined
	const [directReports, setDirectReports] = useState<Employee[] | undefined>([])
	const [workItems, setWorkItems] = useState<WorkItem[]>()
	const [, setLastId] = useState<number | undefined>()
	const [loadingData, setLoadingData] = useState(false)
	const { id: userId } = useTokenClaims()
	const [selectedWorker, setSelectedWorker] = useState<Employee>()
	const [currentEmployee, setCurrentEmployee] = useState<Employee>()
	const [wiSelectMode, setWiSelectMode] = useState<'unassign' | 'assign' | null>(null)
	const [selectedWorkItems, setSelectedWorkItems] = useState<number[]>([])
	const selectedWorkItemsRef = useRef<number[]>([])
	const loadRequestIdRef = useRef(0)
	const [statusFilter, setStatusFilter] = useState<WorkItemStatus>('Unassigned')
	const { hasPermission, permissionsReady } = usePermissionContext()
	const [wiCount, setWiCount] = useState<number>(0)
	const [page, setPage] = useState<number>()
	const [searchText, setSearchText] = useState<string>('')
	const [selectedWorkerSearchHighlightIds, setSelectedWorkerSearchHighlightIds] = useState<
		ReadonlySet<number>
	>(new Set())

	// Children load with the query the top list was loaded with.
	const workItemQuery = useRef<{ workerId: number; status: WorkItemStatus; searchText: string }>()
	const workItemTree = useLazyWorkItemTree(
		useCallback((workItemId: number) => {
			const query = workItemQuery.current!
			return getEmployeeWorkItemChildren(
				query.status,
				query.workerId,
				workItemId,
				query.searchText,
			)
		}, []),
	)
	const selectedWorkerId = useRef<number>()
	const selectedWorkerTree = useLazyWorkItemTree(
		useCallback(
			(workItemId: number) => getDirectReportWorkItemChildren(selectedWorkerId.current!, workItemId),
			[],
		),
	)

	useEffect(() => {
		selectedWorkerId.current = selectedWorker?.id
		selectedWorkerTree.reset()
	}, [selectedWorker])

	useEffect(() => {
		const workerId = selectedWorker?.id
		if (!workerId || !selectedWorker?.workItems || !searchText.trim()) {
			setSelectedWorkerSearchHighlightIds(new Set())
			return
		}
		let active = true
		getDirectReportSearchMatches(workerId, searchText.trim())
			.then((ids) => {
				if (active) setSelectedWorkerSearchHighlightIds(new Set(ids))
			})
			.catch(() => undefined)
		return () => {
			active = false
		}
	}, [selectedWorker, searchText])

	// Selection works on loaded rows; rows can only be ticked once they are on screen.
	const descendantMap = useMemo(() => {
		const map = buildLoadedDescendantMap(workItems ?? [], workItemTree.childrenById)
		return buildLoadedDescendantMap(
			selectedWorker?.workItems ?? [],
			selectedWorkerTree.childrenById,
			map,
		)
	}, [workItems, selectedWorker, workItemTree.childrenById, selectedWorkerTree.childrenById])
	const adjacentSelectedIds = useMemo(() => {
		return selectedWorkItems.flatMap((id) => descendantMap.get(id) ?? [])
	}, [selectedWorkItems, descendantMap])

	useEffect(() => {
		selectedWorker && localStorage.setItem('lastId', selectedWorker.id.toString())
	}, [selectedWorker])
	useEffect(() => {
		selectedWorkItemsRef.current = selectedWorkItems
	}, [selectedWorkItems])
	useEffect(() => {
		setSelectedWorkItems([])
		setWiSelectMode(null)
	}, [currentEmployee?.id])
	useEffect(() => {
		if (wiSelectMode === 'unassign') {
			setSelectedWorkItems([])
			setWiSelectMode(null)
		}
	}, [selectedWorker?.id])

	const addSelectedWorkItem = useCallback(
		(workItemId: number) => {
			const descendants = descendantMap.get(workItemId) ?? []
			const alreadySelectedDescendants = descendants.filter((id) => selectedWorkItems.includes(id))

			setSelectedWorkItems((prev) => [
				...prev.filter((id) => !alreadySelectedDescendants.includes(id)),
				workItemId,
			])
		},
		[descendantMap, selectedWorkItems],
	)

	const removeSelectedWorkItem = useCallback((workItemId: number) => {
		setSelectedWorkItems((prev) => prev.filter((id) => id !== workItemId))
	}, [])

	const loadData = useCallback(
		async (unassignPage?: boolean) => {
			if (!permissionsReady) {
				return
			}
			const requestId = ++loadRequestIdRef.current
			setWorkItems([])
			setLoadingData(true)
			workItemTree.reset()
			const idParam = unassignPage ? undefined : pageWorkerId
			const param =
				idParam !== undefined && idParam !== null && !Number.isNaN(idParam)
					? idParam
					: hasPermission(75)
					? 0
					: userId
			workItemQuery.current = { workerId: param, status: statusFilter, searchText }

			getEmployeeWorkItems(statusFilter, param, page, searchText)
				.then((result) => {
					if (requestId !== loadRequestIdRef.current) return
					setWorkItems(result.employee.workItems)
					setCurrentEmployee(result.employee)
					setDirectReports(result.directReports)
					setWiCount(result.allWorkItemsCount)
					setSelectedWorker(
						result.directReports.find((w) => w.id === Number(localStorage.getItem('lastId'))),
					)
				})
				.finally(() => {
					if (requestId === loadRequestIdRef.current) {
						setLoadingData(false)
					}
				})
		},
		[
			permissionsReady,
			pageWorkerId,
			hasPermission,
			userId,
			statusFilter,
			page,
			searchText,
			workItemTree.reset,
		],
	)

	const unassignWorkItem = useCallback(
		async (workItem: WorkItem, fromEmployeeId: number) => {
			setLoadingData(true)
			try {
				const result = await unassignWork(
					selectedWorkItemsRef.current.length > 0 ? selectedWorkItemsRef.current : [workItem.id],
					fromEmployeeId,
				)
				await loadData()
				setSelectedWorkItems([])
				setWiSelectMode(null)
				return result
			} finally {
				setLoadingData(false)
			}
		},
		[loadData],
	)

	const assignWorkItems = useCallback(
		async (
			workItemId: number,
			toEmployeeId: number,
			fromEmployeeId: number,
			assignAction: boolean,
		): Promise<boolean> => {
			const result = await assignWork(
				selectedWorkItemsRef.current.length > 0 ? selectedWorkItemsRef.current : [workItemId],
				toEmployeeId,
				fromEmployeeId,
				assignAction,
			)
			await loadData()
			setSelectedWorkItems([])
			setWiSelectMode(null)
			return result
		},
		[loadData],
	)

	//#region Search

	useEffect(() => {
		if (!page || !permissionsReady) return
		loadData()
	}, [page, pageWorkerId, searchText, statusFilter, permissionsReady, loadData])

	//#endregion

	const contextValue = useMemo<AssignWorkContextType>(
		() => ({
			directReports,
			workItems,
			workItemTree,
			selectedWorkerTree,
			selectedWorkerSearchHighlightIds,
			unassignWorkItem,
			assignWorkItems,
			loadData,
			loadingData,
			selectedWorker,
			setSelectedWorker,
			setPage,
			setSearchText,
			wiCount,
			page,
			setLastId,
			searchText,
			currentEmployee,
			wiSelectMode,
			setWiSelectMode,
			selectedWorkItems,
			addSelectedWorkItem,
			removeSelectedWorkItem,
			adjacentSelectedIds,
			statusFilter,
			setStatusFilter,
			setSelectedWorkItems,
		}),
		[
			directReports,
			workItems,
			workItemTree,
			selectedWorkerTree,
			selectedWorkerSearchHighlightIds,
			unassignWorkItem,
			assignWorkItems,
			loadData,
			loadingData,
			selectedWorker,
			wiCount,
			page,
			searchText,
			currentEmployee,
			wiSelectMode,
			selectedWorkItems,
			addSelectedWorkItem,
			removeSelectedWorkItem,
			adjacentSelectedIds,
			statusFilter,
		],
	)

	return <AssignWorkContext.Provider value={contextValue}>{children}</AssignWorkContext.Provider>
}

export { useAssignWorkContext, AssignWorkProvider }
